import { BOT_RESULT_ID, BOT_SPARK_ID } from "testIds";
import React, { useContext } from "react";
import styled, { useTheme } from "styled-components";
import { BasicBotData, BotType, LanguageNameSpaces } from "@shared/types";
import Card from "common/Boxes/Card";
import Checkbox from "common/Checkbox/Checkbox";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import {
  getBotExtraDetailsRoute,
  getTradeExtraDetailsRoute,
} from "utils/routes";
import {
  CardsSelectorContext,
  ContextProps,
} from "@shared/common/CardsList/CardsSelector.context";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import { hexToRGB } from "@shared/utils/misc";
import { sharedLib } from "@shared/services/shared.service";
import {
  getBotOneClickFollowCacheKey,
  isGridPnlBotType,
} from "@shared/features/bots/utils";
import { useCanManageBotType } from "@shared/features/bots/hooks";
import { TRADE_EXTRA_DETAILS_TABS } from "@shared/common/constants.ts";
import { TV_ALERT_SYMBOL } from "@shared/features/bots/constants.ts";
import { AccountObjectType } from "@shared/features/settings/types";
import BotActions from "features/bots/common/BotActions";
import BotChange from "features/bots/common/BotChange";
import BotMainIcon from "features/bots/MyBots/common/BotMainIcon";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import InactiveGroupIcon from "features/configuration/common/InactiveGroupIcon.tsx";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup.tsx";
import NewLookBotNowPanel from "features/bots/NewLook/NewLookBotNowPanel";
import NewLookBotStatus from "features/bots/NewLook/NewLookBotStatus";
import NewLookAreaChart from "features/bots/NewLook/NewLookAreaChart";
import useNewLookGridRange from "features/bots/NewLook/useNewLookGridRange";
import NewLookBotFooter from "features/bots/NewLook/NewLookBotFooter";
import {
  canDrawSparkline,
  NEW_LOOK_BOT_CARD,
  onCardKeyDown,
} from "features/bots/NewLook/newLookBotUtils";
import { BOT_PREVIEW_CARD_ID } from "testIds.ts";
import {
  newLookCardInteractive,
  newLookCardSelected,
  newLookCardSurface,
} from "features/bots/NewLook/newLookStyles";
import {
  NEW_LOOK_RADIUS,
  getNewLookTokens,
  newLookPhoneType,
} from "common/NewLook/tokens.ts";
import { NEW_LOOK_PHONE_GUTTER, phoneMaxWidth } from "common/NewLook/layout.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";

interface NewLookBotCardProps {
  botData: BasicBotData;
  id?: string;
  /**
   * The bot's accumulated profit over the last month. Drawn (signals and
   * TradingView bots) only when it makes a readable chart (see
   * canDrawSparkline).
   */
  sparkline?: number[];
}

/**
 * New-look bot card, one frame for every bot type:
 * 1. Header — logo, name, "group-or-pair · account", status, menu.
 * 2. Headline result — realized PnL (30px for the chart types, smaller for
 *    DCA / grid / hedging, whose live visual leads).
 * 3. The type's main visual — an edge-to-edge PnL area chart (signals,
 *    TradingView), the active deal (DCA), the range bar (grid), the
 *    long / short split (hedging); see NewLookBotNowPanel.
 * 4. Footer — one status line and a link (see NewLookBotFooter).
 * A signals / TradingView bot with nothing traded yet gets a "New" badge
 * and a "waiting for the first signal / alert" panel instead of a $0
 * headline and a flat chart.
 */
const NewLookBotCard = ({ botData, id, sparkline }: NewLookBotCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const { historyPush } = useHistoryWithQuery();
  const { isEditMode, getCheckboxProps } = useContext<
    ContextProps<BasicBotData> | undefined
  >(CardsSelectorContext)!;
  const { isChannelConfiguration } = useContext(MyBotsPageContext) || {};
  const checkboxProps = getCheckboxProps(botData);
  const { isLoading } =
    sharedLib.sharedApi.bots.useSetDefaultOneClickSignalsBotMutation({
      fixedCacheKey: getBotOneClickFollowCacheKey(botData),
    })[1];
  const canManageBotType = useCanManageBotType(botData.bot_type);
  const gridRange = useNewLookGridRange(botData);
  // Phone: the name gets the full header width, the status joins the meta
  // line and the menu sits in the corner (see Head).
  const { baseShouldDisplay: isPhone } = useBottomNavigator();

  const botType = botData.bot_type;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isTradingViewBot = botType === BotType.TRADING_VIEW;
  const useGridPnl = isGridPnlBotType(botType);
  const usesGroup = isSignalsBot || isTradingViewSignalsBot;
  const isSelectable = isEditMode && canManageBotType;
  const isSelected = isEditMode && checkboxProps.isSelected;
  /** The chart types: their history is the main visual. */
  const isChartBot = isSignalsBot || isTradingViewBot;
  const openTrades = botData.number_of_open_trades || 0;
  const closedTrades = botData.number_of_closed_trades || 0;
  // DCA / grid / hedging: no closed deals and nothing realized yet.
  const hasNoResult =
    !isChartBot &&
    (isGridPnlBotType(botType)
      ? !(botData.take_profits_filled || botData.number_of_rebalances) &&
        !botData.realised_pnl?.USD
      : closedTrades === 0 && !botData.change?.[0]);
  const isNewBot =
    (isChartBot && closedTrades === 0 && openTrades === 0) || hasNoResult;
  /** Open trades but nothing closed yet: the unrealized PnL leads. */
  const leadsWithUnrealized =
    isChartBot && closedTrades === 0 && openTrades > 0;

  const openBotExtraDetailsDrawer = () => {
    historyPush(
      getBotExtraDetailsRoute(location.pathname, botData.id.toString())
    );
  };

  const onLongPress = () => {
    if (botData.active_trade && location.pathname) {
      historyPush(
        getTradeExtraDetailsRoute(
          location.pathname,
          botData.active_trade.id,
          TRADE_EXTRA_DETAILS_TABS.TIMELINE
        )
      );
    }
  };

  let symbol = botData.symbols?.[0];
  if (!symbol && (isTradingViewBot || isTradingViewSignalsBot)) {
    symbol = TV_ALERT_SYMBOL;
  }

  const phoneMeta = [
    usesGroup ? botData.sub_group_name : symbol,
    !isChannelConfiguration && !isTradingViewSignalsBot
      ? botData.client_name
      : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

  const groupName = (
    <InactiveGroupIcon
      isInactive={isSignalsBot && botData.sub_group_is_active === false}
      style={GROUP_STYLE}
    >
      <Ellipsis title={botData.sub_group_name}>
        {botData.sub_group_name}
      </Ellipsis>
    </InactiveGroupIcon>
  );

  const getCardActions = () => {
    if (isSelectable) {
      return (
        <CheckboxWrapper
          onClick={(e: React.MouseEvent) => {
            checkboxProps.onClick();
            e.stopPropagation();
          }}
        >
          <Checkbox checked={checkboxProps.isSelected} $size={20} />
        </CheckboxWrapper>
      );
    }
    return (
      <MenuWrapper onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <BotActions bot={botData} isCard={true} />
      </MenuWrapper>
    );
  };

  /** The headline result and, for the chart types, the PnL area chart. */
  const renderResult = () => {
    if (isNewBot) return null;
    if (isTradingViewSignalsBot) {
      // These bots forward alerts as signals: where they send them is
      // their result.
      return (
        <Result data-testid={BOT_RESULT_ID}>
          <Hero>
            <ExchangesIconsGroup
              exchanges={botData.exchanges}
              size={24}
              maxCount={6}
            />
          </Hero>
          <HeroCaption>{t("labels.exchanges")}</HeroCaption>
        </Result>
      );
    }
    const caption = leadsWithUnrealized ? (
      t("card.no_closed_trades_yet")
    ) : (
      <>
        {t("labels.realized_pnl")}
        <Dot />
        <Count>
          {useGridPnl
            ? (botType === BotType.HEDGING
                ? botData.number_of_rebalances
                : botData.take_profits_filled) || 0
            : closedTrades}
        </Count>
        &nbsp;
        {(useGridPnl
          ? t(
              botType === BotType.HEDGING
                ? "labels.rebalances"
                : "labels.take_profits_filled"
            )
          : t("labels.closed_trades")
        ).toLowerCase()}
      </>
    );
    // The chart takes the headline's direction (the series' own by default).
    const realizedUsd = useGridPnl
      ? botData.realised_pnl?.USD
      : botData.change?.[0];
    const showChart =
      isChartBot && !leadsWithUnrealized && canDrawSparkline(sparkline);
    return (
      <Result data-testid={BOT_RESULT_ID}>
        <Hero>
          <BotChange
            botData={botData}
            isRealizedPnl={useGridPnl}
            isOpenTrades={leadsWithUnrealized}
          />
        </Hero>
        <HeroCaption>{caption}</HeroCaption>
        {showChart && (
          <Spark data-testid={BOT_SPARK_ID}>
            <NewLookAreaChart
              values={sparkline}
              height={NEW_LOOK_BOT_CARD.chartHeight}
              isUp={realizedUsd === undefined ? undefined : realizedUsd >= 0}
              showEndDot={false}
            />
          </Spark>
        )}
      </Result>
    );
  };

  return (
    <StyledCard
      onClick={openBotExtraDetailsDrawer}
      onKeyDown={onCardKeyDown(openBotExtraDetailsDrawer)}
      tabIndex={0}
      onLongPress={botData.active_trade ? onLongPress : undefined}
      data-testid={BOT_PREVIEW_CARD_ID}
      id={id}
      $selected={isSelected}
    >
      <LoadingSpinnerOverlay isLoading={isLoading}>
        <Inner>
          <Head>
            <IconCell>
              <BotMainIcon
                botData={botData}
                size={NEW_LOOK_BOT_CARD.logo}
                iconStyle={MAIN_ICON}
                showOneClickDefaultIcon={true}
              />
            </IconCell>
            <Name title={botData.name}>
              <NameText>{botData.name}</NameText>
              {isNewBot && <NewBadge>{t("card.new_badge")}</NewBadge>}
            </Name>
            <HeadEnd>
              {!isPhone && <NewLookBotStatus bot={botData} />}
              {getCardActions()}
            </HeadEnd>
            {isPhone ? (
              <Sub>
                {/* One string, one ellipsis: "ADA/USDT · My-Binance-Futures". */}
                <PhoneMeta
                  title={phoneMeta}
                  $isInactive={
                    isSignalsBot && botData.sub_group_is_active === false
                  }
                >
                  {phoneMeta}
                </PhoneMeta>
                <PhoneStatus>
                  {phoneMeta && <Dot />}
                  <NewLookBotStatus bot={botData} />
                </PhoneStatus>
              </Sub>
            ) : (
              <Sub>
                <SubPart $keep={!usesGroup} $lead={usesGroup}>
                  {usesGroup ? (
                    groupName
                  ) : (
                    <Ellipsis title={symbol}>{symbol}</Ellipsis>
                  )}
                </SubPart>
                {!isChannelConfiguration &&
                  !isTradingViewSignalsBot &&
                  botData.client_name && (
                    <>
                      <Dot />
                      <SubPart>
                        <InactiveAccountIcon
                          hideIcon={true}
                          accountId={botData.client_id}
                          objectType={AccountObjectType.ACCOUNT}
                          validColor={theme.colors.secondaryText}
                        >
                          <Ellipsis
                            title={botData.client_name}
                            style={INHERIT_COLOR}
                          >
                            {botData.client_name}
                          </Ellipsis>
                        </InactiveAccountIcon>
                      </SubPart>
                    </>
                  )}
              </Sub>
            )}
          </Head>
          {!isChannelConfiguration && (
            <Body $isNew={isNewBot}>
              {renderResult()}
              <NewLookBotNowPanel
                botData={botData}
                isNewBot={isNewBot}
                gridRange={gridRange}
              />
            </Body>
          )}
          {!isChannelConfiguration && (
            <NewLookBotFooter botData={botData} isNewBot={isNewBot} />
          )}
        </Inner>
      </LoadingSpinnerOverlay>
    </StyledCard>
  );
};

export default React.memo(NewLookBotCard);

const MAIN_ICON = {
  marginRight: 0,
  borderRadius: NEW_LOOK_BOT_CARD.logoRadius,
};
const GROUP_STYLE = { minWidth: 0, maxWidth: "100%", overflow: "hidden" };
const INHERIT_COLOR = { color: "inherit" };

const StyledCard = styled(Card)<{ $selected?: boolean }>`
  &&& {
    container-type: inline-size;
    position: relative;
    /* Cards in a row share a height (no ragged gaps); the extra space goes
       to the card's visual, not to an empty block. */
    height: 100%;
    padding: 0;
    overflow: hidden;
    ${newLookCardSurface};
    border-radius: ${NEW_LOOK_BOT_CARD.radius}px;
    ${newLookCardInteractive};
    ${(p) => p.$selected && newLookCardSelected};
  }
`;
const Inner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
`;
const Head = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas:
    "icon name end"
    "icon sub end";
  align-items: center;
  column-gap: 14px;
  row-gap: 2px;
  padding: ${NEW_LOOK_BOT_CARD.paddingTop}px ${NEW_LOOK_BOT_CARD.padding}px 0;
  min-width: 0;
  @media (max-width: ${phoneMaxWidth}) {
    grid-template-columns: auto minmax(0, 1fr);
    grid-template-areas:
      "icon name"
      "icon sub";
    column-gap: 12px;
    padding: 18px ${NEW_LOOK_PHONE_GUTTER}px 0;
  }
`;
const IconCell = styled.div`
  grid-area: icon;
  display: flex;
`;
const Name = styled.div`
  grid-area: name;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  @media (max-width: ${phoneMaxWidth}) {
    ${newLookPhoneType.cardTitle};
    /* Clear of the corner menu (see HeadEnd). */
    padding-right: 28px;
  }
`;
const NameText = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  /* Phone: the name is the card's id, so it wraps to a second line rather
     than losing most of itself to an ellipsis. */
  @media (max-width: ${phoneMaxWidth}) {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    white-space: normal;
    overflow-wrap: anywhere;
  }
`;
const NewBadge = styled.span`
  flex: none;
  padding: 2px 8px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 500;
  letter-spacing: 0;
  color: ${(p) => getNewLookTokens(p.theme).accent};
  background: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.12)};
`;
const HeadEnd = styled.div`
  grid-area: end;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: -8px;
  @media (max-width: ${phoneMaxWidth}) {
    position: absolute;
    top: 10px;
    right: 4px;
    margin-right: 0;
  }
`;
const Sub = styled.div`
  grid-area: sub;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  @media (max-width: ${phoneMaxWidth}) {
    ${newLookPhoneType.label};
    gap: 0;
  }
`;
const PhoneMeta = styled.span<{ $isInactive?: boolean }>`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${(p) => p.$isInactive && "opacity: 0.6;"}
`;
const PhoneStatus = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
`;
// The pair stays whole (short, and the most useful part). A signals bot's
// group leads: it keeps up to 65% of the line and the account name truncates
// first.
const SubPart = styled.div<{ $keep?: boolean; $lead?: boolean }>`
  flex: ${(p) => (p.$keep ? "none" : p.$lead ? "0 0 auto" : "0 1 auto")};
  min-width: 0;
  max-width: ${(p) => (p.$keep ? "none" : p.$lead ? "65%" : "100%")};
  display: flex;
  overflow: hidden;
  > * {
    min-width: 0;
  }
`;
const Dot = styled.span`
  flex: none;
  display: inline-block;
  margin: 0 6px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  opacity: 0.7;
  &::before {
    content: "·";
  }
`;
const MenuWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const CheckboxWrapper = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
/**
 * The result, then the "now" section (which takes the spare height of an
 * equal-height row; see NowSection).
 */
const Body = styled.div<{ $isNew?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0 ${NEW_LOOK_BOT_CARD.padding}px
    ${(p) => (p.$isNew ? 18 : NEW_LOOK_BOT_CARD.paddingBottom)}px;
  @media (max-width: ${phoneMaxWidth}) {
    padding-left: ${NEW_LOOK_PHONE_GUTTER}px;
    padding-right: ${NEW_LOOK_PHONE_GUTTER}px;
  }
`;
const Result = styled.div`
  min-width: 0;
  margin-top: 18px;
  @media (max-width: ${phoneMaxWidth}) {
    margin-top: 14px;
  }
`;
const Hero = styled.div`
  display: flex;
  min-width: 0;
  /* Never above 18 inside a card; the phone figure step on a phone. */
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  @media (max-width: ${phoneMaxWidth}) {
    ${newLookPhoneType.figure};
  }
`;
const HeroCaption = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  margin-top: 2px;
  overflow: hidden;
  white-space: nowrap;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  @media (max-width: ${phoneMaxWidth}) {
    ${newLookPhoneType.label};
  }
`;
const Count = styled.span`
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
/** Edge to edge, straight under the result line (and over the hairline). */
const Spark = styled.div`
  margin: 16px -${NEW_LOOK_BOT_CARD.padding}px -20px;
  @media (max-width: ${phoneMaxWidth}) {
    margin-left: -${NEW_LOOK_PHONE_GUTTER}px;
    margin-right: -${NEW_LOOK_PHONE_GUTTER}px;
  }
`;
const Ellipsis = styled.span`
  display: block;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
